import * as imgUtil from "@shared/imgUtil.js";
import { renderSettings, bgImgDownscaling } from "@shared/RenderSettings.js";
import { Product } from "@shared/Product.js";
import { CYODesign } from "@shared/CYODesign.js";
import * as imageCache from "@shared/imageCache.js";  // Still needed for user image caching
import { Edge } from "@shared/BorderLoc.js";
import { ContentRendererUtils } from "@shared/ContentRendererUtils.js";
import { ContentRendererCanvas } from "@shared/ContentRendererCanvas.js";
import { ContentRendererUserImage } from "@shared/ContentRendererUserImage.js";
import { ContentRendererPattern } from "@shared/ContentRendererPattern.js";
import { ContentRendererBorderCorner } from "@shared/ContentRendererBorderCorner.js";
import { dlog, dLogTime, dLogTimeEnd } from "@shared/utility.js";
import { RandomState } from "@shared/RandomState.js";
import { RenderSeed } from "@shared/RenderSeed.js";

export const MAX_USER_IMAGE_HEIGHT = 2100;

export class ContentRenderer {
    static Changes = {
        all: "all",
        userImage: "userImage",
        backgroundImage: "backgroundImage",
        stickers: "stickers",
    }

    constructor(device, cyoDesign, printMode = false, randomSeed = null) {
        this.device = device;
        this.cyoDesign = cyoDesign;
        this.printMode = printMode;
        this.stickerImages = [];
        this.stickerImagesUnique = [];
        
        // State for border drawing
        this.borderCounter = 0;
        this.smallUserPhotoForBorder = null;

        this.backgroundImage = null;
        this.transparentImage = null;
        this.userImage = null;

        // When true, sticker placements ignore user image (for split mode wallet surface)
        this.ignorePhotoPlacement = false;

        // When true, don't reserve space for stickers (for split mode grip surface)
        this.ignoreStickerPlacement = false;

        // Optional product images. Default is to load from ./assets/${this.product}.png, etc.
        this.clientProductImage = null;
        this.clientProductMaskImage = null;
        this.clientProductOverlayImage = null;
        
        // Initialize random state
        if (randomSeed !== null) {
            // Print mode: use provided seed for deterministic matching
            this._randomSeed = randomSeed;
            this.randomState = new RandomState(randomSeed);
        } else if (cyoDesign.remixSeed !== null) {
            // Use remix seed for consistent remix results
            this._randomSeed = cyoDesign.remixSeed;
            this.randomState = new RandomState(cyoDesign.remixSeed);
        } else if (cyoDesign.stickerSeed !== null) {
            // Use stored sticker seed to preserve placement across surface mode changes
            this._randomSeed = cyoDesign.stickerSeed;
            this.randomState = new RandomState(cyoDesign.stickerSeed);
        } else {
            if (printMode) {
                // Print mode without seed: generate deterministic seed (fallback)
                const seed = RenderSeed.generateSeed(cyoDesign);
                this._randomSeed = seed;
                this.randomState = new RandomState(seed);
            } else {
                // Preview mode: generate time-based seed and store for future renders
                const timeBasedSeed = Date.now() + Math.floor(Math.random() * 1000);
                this._randomSeed = timeBasedSeed;
                this.randomState = new RandomState(timeBasedSeed);
                // Store seed to CYODesign.current so it persists across surface mode changes
                // Only store if not already set (avoid alt compositor overwriting main's seed)
                if (CYODesign.current.stickerSeed === null) {
                    CYODesign.current.stickerSeed = timeBasedSeed;
                }
            }
        }
        
        // Store reference for debugging
        if (!printMode) {
            window.currentContentRenderer = this;
        } else {
        }
    }

    resetRandomState() {
        this.randomState = new RandomState(this._randomSeed);
    }

    // Helper function to get pattern parameters from device with fallback to RenderSettings
    _getPatternParam(paramName) {
        const productCat = this.device.productCategory;
        
        // Check if device has the parameter and it's not null
        if (this.device[paramName] != null) {
            return this.device[paramName];
        }
        
        // Fall back to RenderSettings
        return renderSettings(productCat, paramName);
    }
       
    async draw() {
        
        dlog(">> draw");
        dLogTime("draw-"+this.device.id);
                
        // Product images should be provided via clientProductImage, clientProductMaskImage
        if (!this.clientProductImage || !this.clientProductMaskImage) {
            throw new Error("Product images must be provided to ContentRenderer");
        }
        
        this.prodImg = this.clientProductImage;
        this.maskImg = this.clientProductMaskImage;
        
        this._setUpCanvases();
        this._setUpUserImage();
        this._setUpBackgroundImage();
        
        this._getPlacements();
                        
        this._drawUserImage();
        this._drawStickers();
        this._drawDebugOverlay();
        
        dLogTimeEnd("draw-"+this.device.id);
        
        return this._makeFinalLayerTree();
    }
    
    redrawUserImage() {
        dlog(">> redrawUserImage");
    
        this.userImageCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this._setUpUserImage();
        this._getPlacements();
        this._drawUserImage();
        this._drawDebugOverlay();

        return this._makeFinalLayerTree();
    }
    
    redrawBackgroundImage() {
        dlog(">> redrawBackgroundImage");
    
        this._getPlacements();
        this.backgroundCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this._setUpBackgroundImage();
        this._drawDebugOverlay();
        
        // Compositing has been moved to Compositor
        
        return this._makeFinalLayerTree();
    }
    
    redrawStickers() {
        dlog(">> redrawStickers");
        
        this.stickersCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.photoStickersCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.stickerImagesUnique = [];
        this._setUpUserImage(); // needed since user photo used for stickers may have changed
        this._getPlacements();
        this._drawStickers();
        this._drawDebugOverlay();
        
        // Compositing has been moved to Compositor
        
        return this._makeFinalLayerTree();
    }


    // Privates:
    
    _setUpCanvases() {
        const canvases = ContentRendererCanvas.setUpCanvases(this.prodImg, this.maskImg);
        
        this.canvasWidth = canvases.canvasWidth;
        this.canvasHeight = canvases.canvasHeight;
        this.backgroundCanvas = canvases.backgroundCanvas;
        this.backgroundCtx = canvases.backgroundCtx;
        this.stickersCanvas = canvases.stickersCanvas;
        this.stickersCtx = canvases.stickersCtx;
        this.photoStickersCanvas = canvases.photoStickersCanvas;
        this.photoStickersCtx = canvases.photoStickersCtx;
        this.userImageCanvas = canvases.userImageCanvas;
        this.userImageCtx = canvases.userImageCtx;
        this.productCanvas = canvases.productCanvas;
        this.productCtx = canvases.productCtx;
        this.maskCanvas = canvases.maskCanvas;
        this.maskCtx = canvases.maskCtx;
        this.debugOverlayCanvas = canvases.debugOverlayCanvas;
        this.debugOverlayCtx = canvases.debugOverlayCtx;
    }
    
    _setUpUserImage() {
        const cyoDesign = this.cyoDesign;
        const product = this.device.productCategory;
        this.userImageHasTransparency = false; // caching for use later
        
        this.userImgVariant = cyoDesign.placementAndStyleRequiresTransparent()
            ? this.transparentImage
            : this.userImage;
    
        // We expect user images to be resized already, but just in case (e.g. testing) let's check and resize if nec
        if (this.userImgVariant && this.userImgVariant.height > MAX_USER_IMAGE_HEIGHT) {
            dlog(`resizing large image! original: ${this.userImgVariant.width} x ${this.userImgVariant.height}`);
            this.userImgVariant = imgUtil.resizeImageOrCanvasForHeight(this.userImgVariant, MAX_USER_IMAGE_HEIGHT);
        }
    
        // Crop out the transparency, if any
        if (this.userImgVariant && imgUtil.isTransparent(this.userImgVariant)) {
            this.userImageHasTransparency = true;
            this.userImgVariant = imgUtil.cropTransparentImage(this.userImgVariant);

            // Check if cropping resulted in a fully transparent (0x0) image
            if (this.userImgVariant.width === 0 || this.userImgVariant.height === 0) {
                console.warn("User image is fully transparent after cropping, skipping");
                this.userImgVariant = null;
            }
        }

        this.userImageFlatEdges = this.userImgVariant ? imgUtil.getFlatEdges(this.userImgVariant) : [];
        dlog(`flat edges: ${this.userImageFlatEdges}`)
        
        // Crop transparent images that have a flat bottom with a circle
        if (cyoDesign.placementAndStyleRequiresTransparent()
            && product != Product.grip // grip circle masking happens in _drawRemoveBackgroundCenterOrBottom()
            && this.userImgVariant != null
            && this.userImageFlatEdges.includes(Edge.bottom)
            && cyoDesign.placementAndStyleRequiresCircleCrop()
            )
        {
            dlog("applying circle crop");
            const srcKey = `${this.transparentImage.src}+circleCrop`;            
            const cachedImg = imageCache.get(srcKey);
            if (cachedImg) {
                this.userImgVariant = cachedImg;
            } else {
                // Use a circle radius 20% bigger than the image's
                const r = (this.userImgVariant.width / 2) * 1.2;
                this.userImgVariant = imgUtil.cropTransparentImage(this.userImgVariant); // crop to new transparent pixels formed through masking circle
                this.userImgVariant = imgUtil.maskImageWithCircle(this.userImgVariant, r);
                imageCache.store(this.userImgVariant, srcKey);
            }
        }
        
        // imgUtil.debugAddImageToHTMLBody(this.userImgVariant, "user image");
    }
    
    _setUpBackgroundImage() {
        // Add background image if it exists
        if (this.backgroundImage) {
            // Use bleed inset to determine downscaling of the bg image
            const downscale = bgImgDownscaling(this.device);
            dlog(`background behavior; scale = ${downscale}`);

            // Composite the bg image to the canvas, applying downscale
            imgUtil.fitImageToCanvas(this.backgroundImage, this.backgroundCanvas, downscale);
        }
    }

    _getPlacements() {
        const cyoDesign = this.cyoDesign;

        this.placements = { stickers: {}, photo: {} };

        // When ignorePhotoPlacement is true (split mode wallet surface), don't reserve space for user image
        const considerPhoto = !this.ignorePhotoPlacement;
        // When ignoreStickerPlacement is true (split mode grip surface), don't reserve space for stickers
        const considerStickers = !this.ignoreStickerPlacement;

        this.placements.photo.border = considerPhoto && this.userImgVariant && cyoDesign.subjectPlacement == CYODesign.Placement.border;
        this.placements.photo.corner = considerPhoto && this.userImgVariant && cyoDesign.subjectPlacement == CYODesign.Placement.corner;
        this.placements.photo.pattern = considerPhoto && this.userImgVariant && cyoDesign.subjectPlacement == CYODesign.Placement.pattern;

        this.placements.stickers.border = considerStickers && cyoDesign.stickerSet != null
            && cyoDesign.stickerPlacement == CYODesign.Placement.border
            && this.stickerImages.length > 0;
        this.placements.stickers.corner = considerStickers && cyoDesign.stickerSet != null
            && cyoDesign.stickerPlacement == CYODesign.Placement.corner
            && this.stickerImages.length > 0;
        this.placements.stickers.pattern = considerStickers && cyoDesign.stickerSet != null
            && cyoDesign.stickerPlacement == CYODesign.Placement.pattern
            && this.stickerImages.length > 0;

        this.placements.someBorder = this.placements.photo.border || this.placements.stickers.border;
        this.placements.someCorner = this.placements.photo.corner || this.placements.stickers.corner;
        this.placements.somePattern = this.placements.photo.pattern || this.placements.stickers.pattern;
    }
    
    _drawUserImage() {
        if (this.userImgVariant == null) {
            this.userImageCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
            return;
        }
        
        dlog(">> _drawUserImage");

        const cyoDesign = this.cyoDesign;
        const product = this.device.productCategory;

        // Force snapshot style to "full" if product is grip or wallet.
        var actualStyle = cyoDesign.subjectStyle;
        if (actualStyle == CYODesign.Style.snapshot && (product == Product.grip || product == Product.wallet)) {
            actualStyle = CYODesign.Style.full;
        }

        if (actualStyle == CYODesign.Style.full) {
            ContentRendererUserImage.drawFullImage(this.userImageCtx, this.userImgVariant, this.device, this.canvasWidth, this.canvasHeight, this.cyoDesign);
        }
        
        if (actualStyle == CYODesign.Style.snapshot) {
            ContentRendererUserImage.drawSnapshot(this.userImageCtx, this.userImgVariant, this.device, this.canvasWidth, this.canvasHeight, this.cyoDesign, this.randomState);
        }
        
        // see if we should place the uploaded image without background
        if (actualStyle == CYODesign.Style.removeBackground 
            && (cyoDesign.subjectPlacement == CYODesign.Placement.center 
                || cyoDesign.subjectPlacement == CYODesign.Placement.bottom))
        {
            ContentRendererUserImage.drawRemoveBackgroundCenterOrBottom(this.userImageCtx, this.userImgVariant, this.userImageFlatEdges, this.device, this.canvasWidth, this.canvasHeight, this.cyoDesign);
        }
    }
    
    _drawStickers() {
        dlog(">> _drawStickers");
        
        // Only do this when there are stickers+pattern, and only if the sticker set has a patternSpacingRatio set.
        // Currently, don't do this if the photo is also "pattern" since the alg tends to draw these too close to each other.
        function shouldUseRandomizedPatternPlacement() {   
            const cyoDesign = this.cyoDesign;
                 
            return cyoDesign.stickerPlacement == CYODesign.Placement.pattern
                && (cyoDesign.subjectPlacement != CYODesign.Placement.pattern || this.userImgVariant == null)
                && cyoDesign.stickerSet.patternSpacingRatio != null;
        }

        if (this.placements.somePattern) {
            if (shouldUseRandomizedPatternPlacement.bind(this)()) {
                ContentRendererPattern.drawPatternRandomAlg(
                    this.stickersCtx, this.photoStickersCtx, this.userImgVariant, this.userImageFlatEdges,
                    this.device, this.canvasWidth, this.canvasHeight, this.cyoDesign, 
                    this.stickerImages, this.placements, this._popSticker.bind(this), this.randomState
                );
            } else {
                ContentRendererPattern.drawPatternGridAlg(
                    this.stickersCtx, this.photoStickersCtx, this.userImgVariant, this.userImageFlatEdges,
                    this.device, this.canvasWidth, this.canvasHeight, this.cyoDesign, 
                    this.stickerImages, this.placements, this._getPatternParam.bind(this), this._popSticker.bind(this), this.randomState
                );
            }
        }
        
        if (this.placements.someCorner) {
            const result = ContentRendererBorderCorner.drawCorner(
                this.stickersCtx, this.photoStickersCtx, this.userImgVariant,
                this.device, this.canvasWidth, this.canvasHeight, this.cyoDesign,
                this.stickerImages, this.placements, this.stickerImagesUnique, this._popSticker.bind(this), this.randomState
            );
            this.stickerImagesUnique = result.stickerImagesUnique;
        }
        // Call drawBorder when:
        // - someBorder is true AND no corner placements (original behavior), OR
        // - photo.border is true (needs drawBorder even when stickers are in corner)
        if (this.placements.someBorder && (!this.placements.someCorner || this.placements.photo.border)) {
            const result = ContentRendererBorderCorner.drawBorder(
                this.stickersCtx, this.photoStickersCtx, this.userImgVariant,
                this.device, this.canvasWidth, this.canvasHeight, this.cyoDesign,
                this.stickerImages, this.placements, this.stickerImagesUnique, this._popSticker.bind(this),
                this._estimateStickerBorderHeight.bind(this), this._estimateUserImageBorderHeight.bind(this), this.randomState
            );
            this.stickerImagesUnique = result.stickerImagesUnique;
            this.smallUserPhotoForBorder = result.smallUserPhotoForBorder;
        }
    }
        
    _makeFinalLayerTree() {
        const layerData = {
            backgroundCanvas: this.backgroundCanvas,
            userImageCanvas: this.userImageCanvas,
            stickersCanvas: this.stickersCanvas,
            photoStickersCanvas: this.photoStickersCanvas,
            productCanvas: this.productCanvas,
            maskCanvas: this.maskCanvas,
            debugOverlayCanvas: this.debugOverlayCanvas
        };
        
        // In print mode, exclude product and debug overlay from the final result
        // but keep mask for proper masking
        if (this.printMode) {
            layerData.productCanvas = null;
            layerData.debugOverlayCanvas = null;
        }
        
        return ContentRendererCanvas.makeFinalLayerTree(layerData, this.cyoDesign);
    }
    
    // **************************
    // Helper methods
    // **************************
    
    // Returns a random sticker from stickerImages that meets the optional filterClosure criteria.
    _popSticker(filterClosure) {
        if (this.stickerImagesUnique.length === 0) {
            this.stickerImagesUnique = this.stickerImages.concat();
        }
        if (typeof filterClosure === "function") {
            // Randomize the remaining indexes in stickerImagesUnique and return the first that matches
            // the filterClosure criteria.
            const idxs = ContentRendererUtils.getRandomOrderArray(this.stickerImagesUnique.length, this.randomState);
            if (idxs.length == 0) {
                console.warn("*** no sticker images");
                return [];
            }
            for (var i=0; i<idxs.length; i++) {
                const nextIdx = idxs[i];
                const stickerImg = this.stickerImagesUnique[nextIdx];
                if (filterClosure(stickerImg)) {
                    this.stickerImagesUnique.splice(nextIdx, 1);
                    return [nextIdx, stickerImg];
                }
            }
            
            // If reached here, none of the remaining stickers met the criteria. Re-populate and call recursively.
            this.stickerImagesUnique = this.stickerImages.concat();
            return this._popSticker(filterClosure);
        } else {
            // simpler case
            const idx = this.randomState.randomInt(0, this.stickerImagesUnique.length);
            const stickerImg = this.stickerImagesUnique[idx];
            this.stickerImagesUnique.splice(idx, 1);
            return [idx, stickerImg];
        }
    }

    // Helper method to estimate sticker height for spacing calculations on border
    _estimateStickerBorderHeight() {
        const product = this.device.productCategory;
        const cyoDesign = this.cyoDesign;
        const canvasHeight = this.canvasHeight;
        const stickerImages = this.stickerImages;
    
        // Default to 0 if no stickers available
        let estimatedStickerHeight = 0;
    
        if (stickerImages.length > 0) {
            // Get sticker scale for sizing
            const sticker_scale = renderSettings(product, "borderStickerScale");
            // Modify scale if specified in the stickerSet
            const customScale = cyoDesign.stickerSet?.borderStickerScale?.[product];
            const actualScale = customScale || sticker_scale;
    
            // Scale the sticker height to get an estimated rendered height
            const sampleSticker = stickerImages[0];
            estimatedStickerHeight = sampleSticker.height * actualScale * canvasHeight / Math.max(sampleSticker.height, sampleSticker.width);
        }
    
        return estimatedStickerHeight;
    }

    // Helper method to estimate user image height for spacing calculations on border
    _estimateUserImageBorderHeight() {
        const product = this.device.productCategory;
        const canvasHeight = this.canvasHeight;
        const SMALL_USER_PHOTO_FOR_BORDER_HEIGHT = 220;
                
        // Get border sticker scale for sizing
        const sticker_scale = renderSettings(product, "borderStickerScale");
        
        // Apply the same scaling as in _drawNextBorderSticker at line 1044
        // const imgScale = sticker_scale * canvasHeight / Math.max(img.height, img.width);
        const imgScale = sticker_scale * canvasHeight / SMALL_USER_PHOTO_FOR_BORDER_HEIGHT;
        
        return SMALL_USER_PHOTO_FOR_BORDER_HEIGHT * imgScale;;
    }

    // Draws debug overlays (safe area in green, bleed insets in red)
    _drawDebugOverlay() {
        if (!this.cyoDesign.debugOptions.showSafeArea && !this.cyoDesign.debugOptions.showBleedInsets) {
            return;
        }

        // Clear the overlay canvas first
        this.debugOverlayCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

        // Draw safe area debug overlay (green) if enabled
        if (this.cyoDesign.debugOptions.showSafeArea) {
            ContentRendererCanvas.drawSafeAreaDebug(
                this.debugOverlayCtx, 
                this.canvasWidth, 
                this.canvasHeight, 
                this.device.safeAreaInsets
            );
        }

        // Draw bleed insets debug overlay (red) if enabled
        if (this.cyoDesign.debugOptions.showBleedInsets) {
            ContentRendererCanvas.drawBleedInsetsDebug(
                this.debugOverlayCtx, 
                this.canvasWidth, 
                this.canvasHeight, 
                this.device.bleedInsets || [0, 0, 0, 0]
            );
        }
    }

} // end of ContentRenderer class